import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface ContentBlock {
  id: number;
  block_id: string;
  user_id: number;
  content_type: string;
  text_value: string;
  blocks?: string[] | null; // JSON array of children block IDs
  properties?: Record<string, any> | null; // JSON object containing additional metadata
  created_at: string;
  updated_at: string;
}

export interface CreateBlockData {
  user_id: number;
  content_type: string;
  text_value: string;
  block_id?: string; // Optional: frontend can provide block_id
  blocks?: string[]; // Optional: JSON array of children block IDs
  properties?: Record<string, any>; // Optional: JSON object containing additional metadata
}

export interface UpdateBlockData {
  content_type: string;
  text_value: string;
  blocks?: string[]; // Optional: JSON array of children block IDs
  properties?: Record<string, any>; // Optional: JSON object containing additional metadata
}

export interface GetUserBlocksParams {
  content_type?: string;
  page?: number;
  limit?: number;
  sort_order?: 'ASC' | 'DESC';
}

export interface SearchBlocksParams {
  user_id: number;
  query: string;
  content_type?: string;
  page?: number;
  limit?: number;
}

export interface PaginationResponse {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

export interface BlocksResponse {
  success: boolean;
  data: ContentBlock[];
  pagination?: PaginationResponse;
  search_query?: string;
  content_type?: string;
}

export interface SingleBlockResponse {
  success: boolean;
  data: ContentBlock;
  message?: string;
}

export interface BlockStats {
  total_blocks: number;
  content_types: Array<{
    content_type: string;
    count: number;
    first_created: string;
    last_created: string;
  }>;
  unique_content_types: number;
}

export interface BlockStatsResponse {
  success: boolean;
  data: BlockStats;
}

export interface AddChildBlockData {
  child_block_id: string;
}

export interface AddChildBlockResponse {
  success: boolean;
  message: string;
  data: {
    parent_block_id: string;
    child_block_id: string;
    children_blocks: string[];
  };
}

export interface RemoveChildBlockResponse {
  success: boolean;
  message: string;
  data: {
    parent_block_id: string;
    removed_child_block_id: string;
    children_blocks: string[];
  };
}

export interface GetChildrenResponse {
  success: boolean;
  data: {
    parent_block_id: string;
    children_blocks: ContentBlock[];
  };
}

export class BlockService {
  /**
   * Create a new content block
   */
  static async createBlock(blockData: CreateBlockData): Promise<ContentBlock> {
    const response = await makeAuthenticatedRequest('/blocks', {
      method: 'POST',
      body: JSON.stringify(blockData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create content block');
    }

    return response.data;
  }

  /**
   * Get a content block by block_id
   */
  static async getBlockById(blockId: string): Promise<ContentBlock> {
    const response = await makeAuthenticatedRequest(`/blocks/${blockId}`);

    if (!response.success) {
      throw new Error('Failed to fetch content block');
    }

    return response.data;
  }

  /**
   * Get user's content blocks with optional filtering and pagination
   */
  static async getUserBlocks(userId: number, params: GetUserBlocksParams = {}): Promise<BlocksResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.content_type) queryParams.append('content_type', params.content_type);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const url = `/blocks/user/${userId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch user content blocks');
    }

    return response;
  }

  /**
   * Get content blocks by content type for a user
   */
  static async getBlocksByType(userId: number, contentType: string, params: Omit<GetUserBlocksParams, 'content_type'> = {}): Promise<BlocksResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const url = `/blocks/user/${userId}/type/${contentType}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch content blocks by type');
    }

    return response;
  }

  /**
   * Update a content block
   */
  static async updateBlock(blockId: string, updateData: UpdateBlockData): Promise<ContentBlock> {
    const response = await makeAuthenticatedRequest(`/blocks/${blockId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update content block');
    }

    return response.data;
  }

  /**
   * Delete a content block
   */
  static async deleteBlock(blockId: string): Promise<void> {
    const response = await makeAuthenticatedRequest(`/blocks/${blockId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete content block');
    }
  }

  /**
   * Search content blocks by content
   */
  static async searchBlocks(params: SearchBlocksParams): Promise<BlocksResponse> {
    const queryParams = new URLSearchParams();
    
    queryParams.append('user_id', params.user_id.toString());
    queryParams.append('query', params.query);
    if (params.content_type) queryParams.append('content_type', params.content_type);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await makeAuthenticatedRequest(`/blocks/search?${queryParams.toString()}`);

    if (!response.success) {
      throw new Error('Failed to search content blocks');
    }

    return response;
  }

  /**
   * Get content block statistics for a user
   */
  static async getBlockStats(userId: number): Promise<BlockStats> {
    const response = await makeAuthenticatedRequest(`/blocks/user/${userId}/stats`);

    if (!response.success) {
      throw new Error('Failed to fetch block statistics');
    }

    return response.data;
  }

  /**
   * Create a note block
   */
  static async createNoteBlock(userId: number, textValue: string): Promise<ContentBlock> {
    return await this.createBlock({
      user_id: userId,
      content_type: 'note',
      text_value: textValue
    });
  }

  /**
   * Create a note block with frontend-provided block_id
   */
  static async createNoteBlockWithId(userId: number, blockId: string, textValue: string): Promise<ContentBlock> {
    return await this.createBlock({
      user_id: userId,
      content_type: 'note',
      text_value: textValue,
      block_id: blockId
    });
  }

  /**
   * Create a code block
   */
  static async createCodeBlock(userId: number, textValue: string): Promise<ContentBlock> {
    return await this.createBlock({
      user_id: userId,
      content_type: 'code',
      text_value: textValue
    });
  }

  /**
   * Create a quote block
   */
  static async createQuoteBlock(userId: number, textValue: string): Promise<ContentBlock> {
    return await this.createBlock({
      user_id: userId,
      content_type: 'quote',
      text_value: textValue
    });
  }

  /**
   * Create an idea block
   */
  static async createIdeaBlock(userId: number, textValue: string): Promise<ContentBlock> {
    return await this.createBlock({
      user_id: userId,
      content_type: 'idea',
      text_value: textValue
    });
  }

  /**
   * Get all note blocks for a user
   */
  static async getNoteBlocks(userId: number, params: Omit<GetUserBlocksParams, 'content_type'> = {}): Promise<BlocksResponse> {
    return await this.getBlocksByType(userId, 'note', params);
  }

  /**
   * Get all code blocks for a user
   */
  static async getCodeBlocks(userId: number, params: Omit<GetUserBlocksParams, 'content_type'> = {}): Promise<BlocksResponse> {
    return await this.getBlocksByType(userId, 'code', params);
  }

  /**
   * Get all quote blocks for a user
   */
  static async getQuoteBlocks(userId: number, params: Omit<GetUserBlocksParams, 'content_type'> = {}): Promise<BlocksResponse> {
    return await this.getBlocksByType(userId, 'quote', params);
  }

  /**
   * Get all idea blocks for a user
   */
  static async getIdeaBlocks(userId: number, params: Omit<GetUserBlocksParams, 'content_type'> = {}): Promise<BlocksResponse> {
    return await this.getBlocksByType(userId, 'idea', params);
  }

  /**
   * Get recent blocks for a user (last 10)
   */
  static async getRecentBlocks(userId: number, limit: number = 10): Promise<ContentBlock[]> {
    const response = await this.getUserBlocks(userId, {
      limit,
      sort_order: 'DESC'
    });

    return response.data;
  }

  /**
   * Get blocks created today for a user
   */
  static async getTodaysBlocks(userId: number): Promise<ContentBlock[]> {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const response = await this.getUserBlocks(userId, {
      limit: 100,
      sort_order: 'DESC'
    });

    // Filter blocks created today
    return response.data.filter(block => 
      block.created_at.startsWith(today)
    );
  }

  /**
   * Get blocks created in the last week for a user
   */
  static async getRecentWeekBlocks(userId: number): Promise<ContentBlock[]> {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const oneWeekAgoStr = oneWeekAgo.toISOString().split('T')[0];

    const response = await this.getUserBlocks(userId, {
      limit: 100,
      sort_order: 'DESC'
    });

    // Filter blocks created in the last week
    return response.data.filter(block => 
      block.created_at >= oneWeekAgoStr
    );
  }

  /**
   * Get content type count for a user
   */
  static async getContentTypeCount(userId: number): Promise<Record<string, number>> {
    const stats = await this.getBlockStats(userId);
    const typeCount: Record<string, number> = {};
    
    stats.content_types.forEach(type => {
      typeCount[type.content_type] = type.count;
    });

    return typeCount;
  }

  /**
   * Check if user has any blocks of a specific type
   */
  static async hasBlocksOfType(userId: number, contentType: string): Promise<boolean> {
    try {
      const response = await this.getBlocksByType(userId, contentType, { limit: 1 });
      return response.data.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get total block count for a user
   */
  static async getTotalBlockCount(userId: number): Promise<number> {
    const stats = await this.getBlockStats(userId);
    return stats.total_blocks;
  }

  /**
   * Add a child block to a parent block
   */
  static async addChildBlock(parentBlockId: string, childBlockId: string): Promise<AddChildBlockResponse> {
    const response = await makeAuthenticatedRequest(`/blocks/${parentBlockId}/children`, {
      method: 'POST',
      body: JSON.stringify({ child_block_id: childBlockId })
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to add child block');
    }

    return response;
  }

  /**
   * Remove a child block from a parent block
   */
  static async removeChildBlock(parentBlockId: string, childBlockId: string): Promise<RemoveChildBlockResponse> {
    const response = await makeAuthenticatedRequest(`/blocks/${parentBlockId}/children/${childBlockId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to remove child block');
    }

    return response;
  }

  /**
   * Get all child blocks of a parent block
   */
  static async getChildBlocks(parentBlockId: string): Promise<GetChildrenResponse> {
    const response = await makeAuthenticatedRequest(`/blocks/${parentBlockId}/children`);

    if (!response.success) {
      throw new Error('Failed to retrieve child blocks');
    }

    return response;
  }

  /**
   * Create a block with children
   */
  static async createBlockWithChildren(blockData: CreateBlockData, childrenBlockIds: string[]): Promise<ContentBlock> {
    const blockDataWithChildren = {
      ...blockData,
      blocks: childrenBlockIds
    };

    return await this.createBlock(blockDataWithChildren);
  }

  /**
   * Update a block with new children
   */
  static async updateBlockWithChildren(blockId: string, updateData: UpdateBlockData, childrenBlockIds: string[]): Promise<ContentBlock> {
    const updateDataWithChildren = {
      ...updateData,
      blocks: childrenBlockIds
    };

    return await this.updateBlock(blockId, updateDataWithChildren);
  }

  /**
   * Get blocks that have children (parent blocks)
   */
  static async getParentBlocks(userId: number, params: GetUserBlocksParams = {}): Promise<ContentBlock[]> {
    const response = await this.getUserBlocks(userId, params);
    
    // Filter blocks that have children
    return response.data.filter(block => 
      block.blocks && block.blocks.length > 0
    );
  }

  /**
   * Get blocks that are children of other blocks
   */
  static async getChildBlocksOnly(userId: number, params: GetUserBlocksParams = {}): Promise<ContentBlock[]> {
    const response = await this.getUserBlocks(userId, params);
    
    // Filter blocks that have no children (leaf blocks)
    return response.data.filter(block => 
      !block.blocks || block.blocks.length === 0
    );
  }

  /**
   * Get the hierarchy depth of a block (how many levels deep it is)
   */
  static async getBlockHierarchyDepth(blockId: string): Promise<number> {
    let depth = 0;
    let currentBlockId = blockId;
    const visited = new Set<string>();

    while (currentBlockId && !visited.has(currentBlockId)) {
      visited.add(currentBlockId);
      
      try {
        const block = await this.getBlockById(currentBlockId);
        if (block.blocks && block.blocks.length > 0) {
          // This block has children, so we can go deeper
          // For simplicity, we'll use the first child to continue the path
          currentBlockId = block.blocks[0];
          depth++;
        } else {
          // No more children, we've reached the end
          break;
        }
      } catch (error) {
        // Block not found or error, stop traversal
        break;
      }
    }

    return depth;
  }

  /**
   * Get all blocks in a hierarchy (parent and all descendants)
   */
  static async getBlockHierarchy(blockId: string): Promise<ContentBlock[]> {
    const hierarchy: ContentBlock[] = [];
    const visited = new Set<string>();

    const traverse = async (currentBlockId: string) => {
      if (visited.has(currentBlockId)) {
        return; // Avoid infinite loops
      }

      visited.add(currentBlockId);

      try {
        const block = await this.getBlockById(currentBlockId);
        hierarchy.push(block);

        if (block.blocks && block.blocks.length > 0) {
          // Recursively get all children
          for (const childId of block.blocks) {
            await traverse(childId);
          }
        }
      } catch (error) {
        // Block not found, skip it
        console.warn(`Block ${currentBlockId} not found during hierarchy traversal`);
      }
    };

    await traverse(blockId);
    return hierarchy;
  }

  /**
   * Update block properties
   */
  static async updateBlockProperties(blockId: string, properties: Record<string, any>): Promise<ContentBlock> {
    const block = await this.getBlockById(blockId);
    
    return await this.updateBlock(blockId, {
      content_type: block.content_type,
      text_value: block.text_value,
      blocks: block.blocks || undefined,
      properties: properties
    });
  }

  /**
   * Merge properties with existing block properties
   */
  static async mergeBlockProperties(blockId: string, newProperties: Record<string, any>): Promise<ContentBlock> {
    const block = await this.getBlockById(blockId);
    const existingProperties = block.properties || {};
    const mergedProperties = { ...existingProperties, ...newProperties };
    
    return await this.updateBlock(blockId, {
      content_type: block.content_type,
      text_value: block.text_value,
      blocks: block.blocks || undefined,
      properties: mergedProperties
    });
  }

  /**
   * Remove specific properties from a block
   */
  static async removeBlockProperties(blockId: string, propertyKeys: string[]): Promise<ContentBlock> {
    const block = await this.getBlockById(blockId);
    const existingProperties = block.properties || {};
    
    // Remove specified keys
    const updatedProperties = { ...existingProperties };
    propertyKeys.forEach(key => {
      delete updatedProperties[key];
    });
    
    return await this.updateBlock(blockId, {
      content_type: block.content_type,
      text_value: block.text_value,
      blocks: block.blocks || undefined,
      properties: Object.keys(updatedProperties).length > 0 ? updatedProperties : undefined
    });
  }

  /**
   * Get blocks that have specific properties
   */
  static async getBlocksWithProperties(userId: number, propertyKey: string, propertyValue?: any): Promise<ContentBlock[]> {
    const response = await this.getUserBlocks(userId, { limit: 1000 });
    
    return response.data.filter(block => {
      if (!block.properties) return false;
      
      if (propertyValue !== undefined) {
        return block.properties[propertyKey] === propertyValue;
      } else {
        return propertyKey in block.properties;
      }
    });
  }

  /**
   * Create a block with properties
   */
  static async createBlockWithProperties(
    blockData: Omit<CreateBlockData, 'properties'>, 
    properties: Record<string, any>
  ): Promise<ContentBlock> {
    return await this.createBlock({
      ...blockData,
      properties
    });
  }
}

export default BlockService;
